import { Action, ActionReducer, createFeature, createReducer, on } from '@ngrx/store';

import * as PurchasePlannerActions from './purchase-planner.actions';
import { initialState, PurchasePlannerState } from './purchase-planner.state';

export const purchasePlannerReducer: ActionReducer<
  PurchasePlannerState,
  Action<string>
> = createReducer(
  initialState,

  on(
    PurchasePlannerActions.loadRawMaterials,
    (state): PurchasePlannerState => ({
      ...state,
      loading: true,
      error: null,
    })
  ),

  on(
    PurchasePlannerActions.loadRawMaterialsSuccess,
    (state, { rawMaterials, details = {} }): PurchasePlannerState => ({
      ...state,
      rawMaterials,
      details: {
        ...state.details,
        ...details,
      },
      filteredMaterials: rawMaterials,
      loading: false,
    })
  ),

  on(
    PurchasePlannerActions.loadRawMaterialsFailure,
    (state, { error }): PurchasePlannerState => ({
      ...state,
      loading: false,
      error,
    })
  ),

  on(
    PurchasePlannerActions.setSearchTerm,
    (state, { searchTerm }): PurchasePlannerState => ({
      ...state,
      filters: {
        ...state.filters,
        searchTerm,
      },
    })
  ),

  on(
    PurchasePlannerActions.setCountryFilter,
    (state, { countries }): PurchasePlannerState => ({
      ...state,
      filters: {
        ...state.filters,
        selectedCountries: countries,
      },
    })
  ),

  on(
    PurchasePlannerActions.setDemandFilter,
    (state, { hasDemand, hasMinDemand }): PurchasePlannerState => ({
      ...state,
      filters: {
        ...state.filters,
        hasDemand,
        hasMinDemand,
      },
    })
  ),

  on(
    PurchasePlannerActions.setTypeFilter,
    (state, { types }): PurchasePlannerState => ({
      ...state,
      filters: {
        ...state.filters,
        selectedTypes: types,
      },
    })
  ),

  on(
    PurchasePlannerActions.setUseArchiveFilter,
    (state, { useArchive }): PurchasePlannerState => ({
      ...state,
      filters: {
        ...state.filters,
        useArchive,
      },
    })
  ),

  on(
    PurchasePlannerActions.loadArchiveEntries,
    (state): PurchasePlannerState => ({
      ...state,
      error: null,
    })
  ),

  on(
    PurchasePlannerActions.loadArchiveEntriesSuccess,
    (state, { archiveEntries }): PurchasePlannerState => ({
      ...state,
      archiveEntries: [...archiveEntries].sort(
        (a, b) => new Date(b.pivotDate).getTime() - new Date(a.pivotDate).getTime()
      ),
    })
  ),

  on(
    PurchasePlannerActions.loadArchiveEntriesFailure,
    (state, { error }): PurchasePlannerState => ({
      ...state,
      error,
    })
  ),

  on(
    PurchasePlannerActions.loadArchiveData,
    (state): PurchasePlannerState => ({
      ...state,
      loading: true,
      error: null,
    })
  ),

  on(
    PurchasePlannerActions.loadArchiveDataSuccess,
    (state, { rawMaterials, details = {} }): PurchasePlannerState => ({
      ...state,
      rawMaterials,
      details: {
        ...state.details,
        ...details,
      },
      filteredMaterials: rawMaterials,
      loading: false,
    })
  ),

  on(
    PurchasePlannerActions.loadArchiveDataFailure,
    (state, { error }): PurchasePlannerState => ({
      ...state,
      loading: false,
      error,
    })
  ),

  on(
    PurchasePlannerActions.setArchiveFilter,
    (state, { archiveId }): PurchasePlannerState => ({
      ...state,
      filters: {
        ...state.filters,
        archiveId,
      },
    })
  ),

  on(
    PurchasePlannerActions.clearFilters,
    (state): PurchasePlannerState => ({
      ...state,
      filters: {
        searchTerm: '',
        selectedCountries: [],
        hasDemand: false,
        hasMinDemand: false,
        selectedTypes: [],
        useArchive: true,
        archiveId: null,
      },
    })
  )
);

export const purchasePlannerFeature: ReturnType<
  typeof createFeature<string, PurchasePlannerState>
> = createFeature({
  name: 'purchasePlanner',
  reducer: purchasePlannerReducer,
});
